import { useState } from 'react'
import { AlertTriangle, FileText, Replace } from 'lucide-react'
import { Button } from '@teach/ui'
import { ArticleInlinePicker } from '@/features/instructions/components/ArticleInlinePicker'
import type { Instruction } from '@/features/instructions/types'
import type { ArticleBlock } from '@/types/blocks'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  block: ArticleBlock
  onChange: (next: ArticleBlock) => void
  isCourseAuthor: boolean
  /** Сохранить экран сразу (выбор статьи в пустом блоке — не повод ждать паузы). */
  onAutoSave?: () => void
}

export const ArticleBlockEditor = ({ block, onChange, isCourseAuthor, onAutoSave }: Props) => {
  const m = useMessages(messages)
  const [pickerOpen, setPickerOpen] = useState(false)

  /**
   * Вложенный `article` кладём в ЧЕРНОВИК вместе с id (п.6): карточка ниже
   * рисуется именно из него, а с сервера он приезжает только после сохранения —
   * до этого автор видел бы прежнюю статью и не понимал, применился ли выбор.
   * В тело `PUT` объект не утечёт: `toArticleCreate` снимает и `article`, и
   * серверный `available` (см. lib/articleBlock.ts).
   *
   * `available: true` — не догадка: пикер показывает только опубликованные
   * статьи (`public`/`link`), поэтому свежевыбранная не может быть снятой с
   * публикации. Ответ сервера всё равно перезапишет признак.
   */
  const handleArticleChange = (newArticleId: number, article: Instruction) => {
    onChange({ ...block, articleId: newArticleId, article, available: true })
    setPickerOpen(false)
  }

  // Блок без статьи (`articleId: null`, PRD #71, ф.3) — сразу список, как в
  // окне выбора, без «Отмены»: возвращаться не к чему. Выбор сохраняется сразу.
  if (block.articleId == null) {
    return (
      <ArticleInlinePicker
        selectedId={null}
        onPick={(id, article) => {
          handleArticleChange(id, article)
          onAutoSave?.()
        }}
        // По центру свободной высоты панели (минус шапка и поиск).
        emptyClassName="flex min-h-[calc(100dvh-30rem)] items-center justify-center"
      />
    )
  }

  const showWarning = !block.available || block.article === null

  // «Поменять статью» — текущая карточка уходит, на её месте весь список
  // (как при выборе в новом блоке) и «Отмена».
  if (pickerOpen) {
    return (
      <div className="space-y-4">
        <ArticleInlinePicker selectedId={block.articleId} onPick={handleArticleChange} />
        <Button variant="outline" size="sm" className="w-full" onClick={() => setPickerOpen(false)}>
          {m.cancel}
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {showWarning ? (
        <section className="rounded-lg border border-warning/30 bg-warning/10 p-4 space-y-2">
          <div className="flex items-center gap-2 text-warning">
            <AlertTriangle className="h-4 w-4" />
            <span className="font-medium">{m.articleUnpublished}</span>
          </div>
          <p className="text-sm text-muted-foreground">{m.articleUnpublishedHint}</p>
          <Button
            onClick={() => setPickerOpen(true)}
            variant="outline"
            size="sm"
            disabled={!isCourseAuthor}
            title={!isCourseAuthor ? m.noRights : undefined}
          >
            <Replace className="h-4 w-4 mr-1.5" />
            {m.changeArticle}
          </Button>
        </section>
      ) : (
        <section className="space-y-3">
          {/* Просто иконка и название — обложку автор видит на холсте. */}
          <div
            data-testid="article-card-row"
            className="flex items-center gap-3 rounded-xl border border-border px-4 py-3"
          >
            <FileText className="size-6 shrink-0 text-brand" />
            <h3 className="min-w-0 truncate text-base font-medium">{block.article!.title}</h3>
          </div>
          <Button
            onClick={() => setPickerOpen(true)}
            variant="outline"
            size="sm"
            className="w-full"
            disabled={!isCourseAuthor}
            title={!isCourseAuthor ? m.noRights : undefined}
          >
            <Replace className="mr-1.5 h-4 w-4" />
            {m.changeArticle}
          </Button>
        </section>
      )}
    </div>
  )
}
